import { useCallback, useState } from "react";
import { useForm } from "react-hook-form";
import { useLocation } from "react-router";
import { sendPasswordResetEmail } from "supertokens-web-js/recipe/emailpassword";

import { AuthenticationPage } from "@/components/AuthenticationPage/AuthenticationPage";
import { useFlashToast } from "@/components/FlashToast/FlashToast";
import { Form } from "@/components/Form/Form";
import { FormInput } from "@/components/Form/FormInput";
import { AlertCircleIcon } from "@/components/Icons/AlertCircleIcon";
import { Button } from "@/design-system/Button";
import { FormField } from "@/design-system/FormField";
import { Inline } from "@/design-system/Inline";
import { Stack } from "@/design-system/Stack";
import { removeFromHeaders } from "@/helpers/headers";
import { createEmailValidRule, createRequiredStringRule } from "@/helpers/rules";
import { sleep } from "@/helpers/sleep";
import { useBool } from "@/hooks/useBool";
import { usePublicAppBranding } from "@/providers/PublicAppBrandingProvider";
import { routes } from "@/routes";
import { Trans, useTranslation } from "@/translations";

enum PageState {
  Initial,
  Sent,
}

export function ForgotPasswordPage(): React.ReactNode {
  const { appBranding } = usePublicAppBranding();
  const { i18n, t } = useTranslation();
  const location = useLocation();
  const showFlashToast = useFlashToast();

  const [isSubmitting, submittingHandlers] = useBool();
  const [state, setState] = useState(PageState.Initial);
  const [submittedEmail, setSubmittedEmail] = useState<string | undefined>();

  const initialEmail = location?.state?.email || "";

  const formMethods = useForm<FormValues>({
    defaultValues: {
      email: initialEmail,
    },
  });
  const { setError } = formMethods;

  const sendResetLink = useCallback(
    async (email: string, resend?: boolean) => {
      submittingHandlers.setTrue();

      // Wait for 1.5 seconds to make sure the user actually sees the spinner
      const sleepPromise = sleep(1500);

      try {
        const response = await sendPasswordResetEmail({
          formFields: [
            {
              id: "email",
              value: email.trim(),
            },
          ],
          options: {
            preAPIHook: (x) => {
              const url = new URL(x.url);
              url.searchParams.set("languageId", i18n.language);
              url.searchParams.set("brand", appBranding);

              return Promise.resolve({
                ...x,
                url: url.toString(),
                requestInit: removeFromHeaders(x.requestInit, "Authorization"),
              });
            },
          },
        });

        setSubmittedEmail(email);

        if (response.status === "FIELD_ERROR") {
          // This should never happen because we do validation on the client side
          setError("email", { type: "manual", message: response.formFields[0].error });
        } else if (response.status === "PASSWORD_RESET_NOT_ALLOWED") {
          // Can only occur when we start using social login.
          // This can happen due to automatic account linking.
          // Please read our account linking docs
          console.error("Password reset not allowed");
          showFlashToast({ variant: "alert", message: t("common.generic-error") });
        } else {
          await sleepPromise;
          setState(PageState.Sent);

          if (resend) {
            showFlashToast({ variant: "success", message: t("page.forgot-password.sent.resend-toast") });
          }
        }
      } catch (err: any) {
        setError("email", {
          type: "manual",
          message: t("common.generic-error"),
        });
        setState(PageState.Initial);
      } finally {
        submittingHandlers.setFalse();
      }
    },
    [i18n.language, setError, showFlashToast, submittingHandlers, t, appBranding],
  );

  return (
    <AuthenticationPage
      header={state === PageState.Sent ? t("page.forgot-password.sent.title") : t("page.forgot-password.title")}
      body={
        state === PageState.Sent ? (
          <p>
            <Trans
              i18nKey="page.forgot-password.sent.description-with-email"
              values={{ email: submittedEmail || "" }}
              components={{ email: <span className="text-text-brand-secondary" /> }}
            />
          </p>
        ) : (
          t("page.forgot-password.description")
        )
      }
    >
      {state === PageState.Initial && (
        <Form.Root formMethods={formMethods} onSubmit={(data) => sendResetLink(data.email)}>
          <Stack gap="xl">
            <FormField label={t("page.forgot-password.email")}>
              <FormInput<FormValues, "email">
                id="email"
                name="email"
                type="email"
                required
                rules={{
                  validate: {
                    required: createRequiredStringRule(t, "page.login.form.email.label"),
                    isValidEmail: createEmailValidRule(t),
                  },
                }}
                placeholder={t("page.login.form.email.placeholder")}
                autoComplete="username"
                autoCapitalize="none"
                spellCheck="false"
                aria-required
              />
            </FormField>
            <Button type="submit" fullWidth isLoading={isSubmitting} data-testid="send-recover-pass-btn">
              {t("page.forgot-password.send-reset-link")}
            </Button>
          </Stack>
          <footer className="mt-space-md">
            <Button
              size="sm"
              variant="ghostPrimary"
              type="link"
              href={routes.authentication.login()}
              state={{ email: initialEmail }}
            >
              {t("page.forgot-password.cancel")}
            </Button>
          </footer>
        </Form.Root>
      )}
      {state === PageState.Sent && (
        <Stack gap="lg">
          <aside className="pt-page-bottom">
            <Inline gap="xs" align="start">
              <span className="mt-0.5 size-icon-inline shrink-0">
                <AlertCircleIcon size={16} tone="secondary" />
              </span>
              <span className="text-caption text-text-secondary">{t("page.forgot-password.sent.remark")}</span>
            </Inline>
          </aside>
          <Inline justify="center" wrap={false}>
            <Button
              variant="ghostPrimary"
              onClick={() => sendResetLink(formMethods.getValues("email"), true)}
              isLoading={isSubmitting}
            >
              {t("page.verify-email.resend")}
            </Button>
          </Inline>
        </Stack>
      )}
    </AuthenticationPage>
  );
}

interface FormValues {
  email: string;
}
